import { Kitchen } from '../../../libs';
import { isRecord } from '../../../libs/utils/isRecord';
import { VSS_PATHS, vssGet, VssRequestError } from '../../../libs/vssApi';
import {
  KitchenAlert,
  KitchenCameraListing,
  KitchenCameraStatus,
  KitchenDoorBucket,
  KitchenOccupancyBucket,
  KitchenRoomOccupancy
} from '../types';
import { categoryToType } from './episodes';

/** Camera and analytics coverage exists for this kitchen only; any other kitchen gets the empty answer without a call. */
const SUPPORTED_KITCHEN = Kitchen.BH001;

export const hasKitchenCameras = (kitchen: Kitchen): boolean => kitchen === SUPPORTED_KITCHEN;

const SENSOR_ID = new RegExp(String.raw`^${SUPPORTED_KITCHEN}_\d+_\d{3,5}$`);
// camera_rooms.json forbids / = @ #; ? and % stay out too because the name travels as a query value.
const ROOM = /^[^\p{Cc}/=@#?%]{1,80}$/u;
// Upstream keeps incidents for 48 hours.
const MAX_RANGE_MS = 48 * 60 * 60 * 1000;
const MIN_BUCKETS = 1;
const MAX_BUCKETS = 120;
const MAX_RESULT_SIZE = 200;

const assertValidSensorId = (sensorId: string) => {
  if (!SENSOR_ID.test(sensorId)) {
    throw new Error(`Invalid sensorId: ${sensorId}`);
  }
};

const assertValidRoom = (room: string) => {
  if (!ROOM.test(room)) {
    throw new Error(`Invalid room: ${room}`);
  }
};

const assertValidRange = (from: string, to: string) => {
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) {
    throw new TypeError('`from` and `to` must be ISO 8601 timestamps');
  }
  if (toMs <= fromMs) {
    throw new Error('`to` must be after `from`');
  }
  if (toMs - fromMs > MAX_RANGE_MS) {
    throw new Error('`from`/`to` range exceeds 48 hours');
  }
};

const bucketCount = (buckets: number): string => String(Math.min(Math.max(buckets, MIN_BUCKETS), MAX_BUCKETS));

const malformed = (): never => {
  throw new VssRequestError();
};
const record = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : malformed());
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : malformed());
const text = (source: Record<string, unknown>, key: string): string => {
  const value = source[key];
  return typeof value === 'string' ? value : malformed();
};
const count = (source: Record<string, unknown>, key: string): number => {
  const value = source[key];
  return typeof value === 'number' ? value : malformed();
};
const textOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const alertFromIncident = (value: unknown, kitchen: Kitchen): KitchenAlert => {
  const incident = record(value);
  const category = text(incident, 'category');
  const type = categoryToType(category);
  const info = isRecord(incident.info) ? incident.info : {};
  const { objectIds } = incident;
  return {
    id: text(incident, 'Id'),
    type,
    category,
    sensorId: text(incident, 'sensorId'),
    start: text(incident, 'timestamp'),
    end: text(incident, 'end'),
    complete: info.isComplete === true,
    // A PPE incident holds a stream id in objectIds, not a head count.
    objectCount: type === 'PPE' || !Array.isArray(objectIds) ? null : objectIds.length,
    verdict: textOrNull(info.verdict),
    kitchen
  };
};

const newestFirst = (a: KitchenAlert, b: KitchenAlert): number => Number(b.start > a.start) - Number(b.start < a.start);

/** The calibration's `room` place of each sensor, by sensor name; an entry without a valid name or room is left out. */
const getCalibrationRooms = async (): Promise<Map<string, string>> => {
  const rooms = new Map<string, string>();
  for (const sensor of list(record(await vssGet(VSS_PATHS.calibration)).sensors)) {
    if (!isRecord(sensor) || typeof sensor.id !== 'string' || !SENSOR_ID.test(sensor.id)) {
      continue;
    }
    const places: unknown[] = Array.isArray(sensor.place) ? sensor.place : [];
    const room = places.filter(isRecord).find((place) => place.name === 'room');
    if (typeof room?.value === 'string' && ROOM.test(room.value)) {
      rooms.set(sensor.id, room.value);
    }
  }
  return rooms;
};

/** A calibration that cannot be read leaves every camera listed with no room. */
const withoutRooms = (error: unknown): Map<string, string> => {
  if (error instanceof VssRequestError) {
    return new Map();
  }
  throw error;
};

/** The kitchen's online sensors under a valid name, each with its calibrated room and main stream. */
export const listKitchenCameras = async (kitchen: Kitchen): Promise<KitchenCameraListing[]> => {
  if (!hasKitchenCameras(kitchen)) {
    return [];
  }
  const [sensors, streams, rooms] = await Promise.all([
    vssGet(VSS_PATHS.sensorList),
    vssGet(VSS_PATHS.sensorStreams),
    getCalibrationRooms().catch(withoutRooms)
  ]);
  // VST answers with an array of sensor-uuid maps; every element's keys are merged.
  const streamsByUuid = new Map<string, unknown[]>();
  for (const entry of list(streams)) {
    for (const [uuid, sensorStreams] of Object.entries(record(entry))) {
      streamsByUuid.set(uuid, list(sensorStreams));
    }
  }
  return list(sensors).flatMap((value) => {
    if (!isRecord(value)) {
      return [];
    }
    const { name, sensorId: uuid, state } = value;
    if (state !== 'online' || typeof name !== 'string' || typeof uuid !== 'string' || !SENSOR_ID.test(name)) {
      return [];
    }
    const mainStream = (streamsByUuid.get(uuid) ?? []).map(record).find((stream) => stream.isMain === true);
    return [{ id: name, room: rooms.get(name) ?? null, streamId: mainStream ? text(mainStream, 'streamId') : null }];
  });
};

export const getKitchenCameraStatus = async (kitchen: Kitchen, sensorId: string): Promise<KitchenCameraStatus> => {
  if (!hasKitchenCameras(kitchen)) {
    return { sensorId, lastProcessedAt: null };
  }
  assertValidSensorId(sensorId);
  const { latestTimestamp } = record(await vssGet(VSS_PATHS.lastProcessedTimestamp, { sensorId }));
  return { sensorId, lastProcessedAt: isRecord(latestTimestamp) ? text(latestTimestamp, 'timestamp') : null };
};

export const getKitchenRoomOccupancy = async (kitchen: Kitchen, room: string): Promise<KitchenRoomOccupancy | null> => {
  if (!hasKitchenCameras(kitchen)) {
    return null;
  }
  assertValidRoom(room);
  const body = record(await vssGet(VSS_PATHS.roomOccupancy, { place: `room=${room}`, objectType: 'Person' }));
  return { room, people: count(body, 'occupancy'), at: text(body, 'timestamp') };
};

/** The incidents of the range, newest first, each with its VLM verdict when upstream has one. */
export const listKitchenAlerts = async (kitchen: Kitchen, from: string, to: string): Promise<KitchenAlert[]> => {
  if (!hasKitchenCameras(kitchen)) {
    return [];
  }
  assertValidRange(from, to);
  const range = { fromTimestamp: from, toTimestamp: to, maxResultSize: String(MAX_RESULT_SIZE) };
  const [primary, verified] = await Promise.all([
    vssGet(VSS_PATHS.incidents, range),
    vssGet(VSS_PATHS.incidents, { vlmVerified: 'true', vlmVerdict: 'all', ...range })
  ]);
  const byId = new Map<string, KitchenAlert>();
  for (const incident of list(record(primary).incidents)) {
    const alert = alertFromIncident(incident, kitchen);
    byId.set(alert.id, alert);
  }
  for (const incident of list(record(verified).incidents)) {
    const alert = alertFromIncident(incident, kitchen);
    const existing = byId.get(alert.id);
    byId.set(alert.id, existing ? { ...existing, verdict: alert.verdict } : alert);
  }
  return [...byId.values()].sort(newestFirst);
};

export const listKitchenPeopleHistogram = async (
  kitchen: Kitchen,
  sensorId: string,
  from: string,
  to: string,
  buckets: number
): Promise<KitchenOccupancyBucket[]> => {
  if (!hasKitchenCameras(kitchen)) {
    return [];
  }
  assertValidSensorId(sensorId);
  assertValidRange(from, to);
  const body = record(
    await vssGet(VSS_PATHS.fovHistogram, {
      sensorId,
      fromTimestamp: from,
      toTimestamp: to,
      bucketCount: bucketCount(buckets),
      objectType: 'Person'
    })
  );
  return list(body.histogram).map((value) => {
    const bucket = record(value);
    const person = list(bucket.objects)
      .map(record)
      .find((object) => object.type === 'Person');
    return { start: text(bucket, 'start'), end: text(bucket, 'end'), people: person ? count(person, 'averageCount') : 0 };
  });
};

/** Entries and exits of the Person count per tripwire per bucket; the effective count, not the raw one. */
export const listKitchenDoorActivity = async (
  kitchen: Kitchen,
  sensorId: string,
  from: string,
  to: string,
  buckets: number
): Promise<KitchenDoorBucket[]> => {
  if (!hasKitchenCameras(kitchen)) {
    return [];
  }
  assertValidSensorId(sensorId);
  assertValidRange(from, to);
  const body = record(
    await vssGet(VSS_PATHS.tripwireHistogram, {
      sensorId,
      fromTimestamp: from,
      toTimestamp: to,
      bucketCount: bucketCount(buckets),
      objectType: 'Person'
    })
  );
  return list(body.tripwires).flatMap((value) => {
    const tripwire = record(value);
    const tripwireId = text(tripwire, 'id');
    return list(tripwire.histogram).map((entry) => {
      const bucket = record(entry);
      const events = list(bucket.events).map(record);
      const personCount = (direction: 'IN' | 'OUT'): number => {
        const event = events.find((candidate) => candidate.type === direction && candidate.objectType === 'Person');
        return event ? count(event, 'count') : 0;
      };
      return {
        tripwireId,
        start: text(bucket, 'start'),
        end: text(bucket, 'end'),
        entries: personCount('IN'),
        exits: personCount('OUT')
      };
    });
  });
};
